/**
 * The mirrored Bexio contact list and its Vantio mappings (`/admin/bexio/contacts`).
 *
 * READ PLUS THE EXISTING MAPPING WRITES, nothing more. This page creates no Bexio
 * contacts, edits none, and deletes none — that would be a Bexio write slice. The
 * only mutations reachable from here are the contact→tenancy mapping calls that
 * already exist, and they are reached through the existing service, not a second
 * implementation.
 *
 * `display_name` IS NOT "the tenant". A Bexio contact can be a company, a couple,
 * a parent paying for a room, or a combined household. The column is labelled
 * "Name" and the mapping section is what says which tenancy it serves, if any.
 *
 * CARDINALITY, because it is easy to get wrong: a TENANCY has at most one Bexio
 * billing contact, while one contact may serve 0..N tenancies. Migration 141
 * dropped the contact-side unique constraint precisely because the reverse rule
 * was wrong. Nothing here may imply one-contact-one-tenancy — and the backend's
 * own `not_contact_level_mappable` status exists to flag contacts that span
 * several tenancies.
 *
 * KPI SCOPE — corrected in UI-4b. These counts used to be computed from the rows
 * on the loaded page, which made "Nicht zugeordnet 2" look like an org-wide fact
 * when it was not. They now come from the list endpoint itself: whether a contact
 * is mapped is the presence of a `bexio_entity_map` row, which is a stored fact
 * and therefore a SQL predicate, so each card is a `total` from the SAME query its
 * click applies. KPI and filter cannot diverge, because they are one query.
 *
 * The finer statuses (`suggested`, `conflict`, `not_contact_level_mappable`) are
 * NOT counted separately: telling them apart needs the name-matching scorer over
 * the org's tenancy views, which is not something a list query can do. They remain
 * on the per-row badge, and the "Ohne Zuordnung" card counts all of them together
 * — which is exactly what it says.
 *
 * WHERE `invalid` LANDS, since it is easy to read the cards as contradicting a row:
 * a mapping whose target tenancy no longer resolves is `invalid` on the badge, and
 * it still HAS a stored mapping row — so it counts under "Zugeordnet". The card
 * hints therefore say "Mit gespeicherter Zuordnung" / "Noch keine Zuordnung
 * gespeichert" rather than claiming a resolvable tenancy: an `invalid` row sitting
 * inside the "Zugeordnet" filter is then consistent with what the card promised,
 * and the badge is what tells the reader the target is broken. Splitting `invalid`
 * out would mean joining tenancies inside the list query — new mapping semantics,
 * which this slice does not invent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users, CheckCircle2, MinusCircle, Search } from "lucide-react";

import {
  fetchBexioContacts,
  getBexioContactMappingStatuses,
} from "../../../api/adminData/bexio";
import { formatDateTime } from "../../../utils/datetime";
import BexioKpiFilterCard from "../../../components/admin/bexio/workspace/BexioKpiFilterCard";
import BexioActiveFilters from "../../../components/admin/bexio/workspace/BexioActiveFilters";
import BexioContactMappingModal from "../../../components/admin/bexio/mapping/BexioContactMappingModal";
import BexioFilterBar, {
  BexioFilterSelect,
} from "../../../components/admin/bexio/workspace/BexioFilterBar";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import BexioDetailDrawer, {
  DrawerField,
  DrawerSection,
} from "../../../components/admin/bexio/workspace/BexioDetailDrawer";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
import { useBexioList } from "../../../components/admin/bexio/workspace/useBexioList";
import { useDebouncedValue } from "../../../components/admin/bexio/workspace/useDebouncedValue";
import {
  MAPPING_STATUS_LABEL,
  mapLabel,
  mappingTone,
} from "../../../components/admin/bexio/bexioFormat";

const TONE_CLASS = {
  good: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

/**
 * Mapping filter values — now server-side, over all pages.
 *
 * "Ohne Zuordnung" is every contact with no confirmed mapping row, which is the
 * union of the service's `unmapped`, `suggested`, `conflict` and
 * `not_contact_level_mappable`. It is named for what it selects rather than
 * borrowing one of those status labels, because it is broader than any of them.
 */
const MAPPING_FILTER_OPTIONS = [
  { value: "", label: "Zuordnung: Alle" },
  { value: "mapped", label: "Zugeordnet" },
  { value: "unmapped", label: "Ohne Zuordnung" },
];

const EMPTY_FILTERS = { q: "", mapping: "" };

/**
 * What the row's mapping button offers.
 *
 * "Zuordnen" only where nothing is assigned. A mapped contact gets "Ändern" —
 * NOT "kann nicht mehr zugeordnet werden": one Bexio contact may serve several
 * tenancies (migration 141 dropped the contact-side uniqueness), so the UI must
 * not imply that an existing assignment closes the door.
 */
function contactMappingActionLabel(status) {
  if (status === "mapped") return "Ändern";
  if (status === "invalid" || status === "conflict") return "Prüfen";
  return "Zuordnen";
}

function MappingPill({ status }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        TONE_CLASS[mappingTone(status)] || TONE_CLASS.neutral
      }`}
    >
      {mapLabel(MAPPING_STATUS_LABEL, status)}
    </span>
  );
}

export default function BexioContactsPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);

  // Lagged copy for the server; the input itself stays immediate.
  const debouncedQ = useDebouncedValue(filters.q);

  const { items, total, loading, error, reload } = useBexioList({
    fetcher: fetchBexioContacts,
    filters: { q: debouncedQ || undefined, mapping: filters.mapping || undefined },
    limit,
    offset,
  });

  // One batched mapping call for the loaded page. Never one call per row.
  const [statuses, setStatuses] = useState({});
  useEffect(() => {
    const ids = (items || []).map((c) => c.bexio_contact_id).filter((v) => v != null);
    if (!ids.length) {
      setStatuses({});
      return;
    }
    let alive = true;
    getBexioContactMappingStatuses(ids)
      .then((res) => {
        if (!alive) return;
        const map = {};
        (res?.items || []).forEach((it) => {
          map[it.bexio_contact_id] = it;
        });
        setStatuses(map);
      })
      .catch(() => {
        if (!alive) return;
        setStatuses({});
      });
    return () => {
      alive = false;
    };
  }, [items]);

  const applyFilters = (next) => {
    setFilters(next);
    setOffset(0);
  };

  /**
   * The card figures, each from the endpoint's own `total`.
   *
   * Three requests with `limit=1` — the rows are thrown away, only the count is
   * wanted — instead of paging the whole set to classify it client-side. Each probe
   * carries exactly the `mapping` value the matching card applies, so a card's
   * number and the list its click opens are by construction ONE query. They follow
   * the search term too, so the figures describe the set the reader is looking at.
   *
   * The "all" probe looks redundant against the list's own `total`, and is not: the
   * list total is narrowed by the ACTIVE mapping filter, so reading the Gesamt card
   * off it made that card show 26 while the "Zugeordnet" card also showed 26 — a
   * card labelled "Gesamt" reporting a subset. Giving all three cards the same
   * provenance is what removes the contradiction instead of papering over it.
   */
  const [allTotal, setAllTotal] = useState(null);
  const [mappedTotal, setMappedTotal] = useState(null);
  const [unmappedTotal, setUnmappedTotal] = useState(null);
  const countsGeneration = useRef(0);
  const loadCounts = useCallback(async (term) => {
    const mine = ++countsGeneration.current;
    const probe = (mapping) =>
      fetchBexioContacts({ q: term || undefined, mapping, limit: 1, offset: 0 });
    try {
      const [a, m, u] = await Promise.all([
        probe(undefined),
        probe("mapped"),
        probe("unmapped"),
      ]);
      if (mine !== countsGeneration.current) return;
      const num = (r) => (typeof r?.total === "number" ? r.total : null);
      setAllTotal(num(a));
      setMappedTotal(num(m));
      setUnmappedTotal(num(u));
    } catch {
      if (mine !== countsGeneration.current) return;
      setAllTotal(null);
      setMappedTotal(null);
      setUnmappedTotal(null);
    }
  }, []);
  useEffect(() => {
    loadCounts(debouncedQ);
  }, [loadCounts, debouncedQ]);

  /** The contact whose mapping modal is open. One modal for the whole page. */
  const [mappingContact, setMappingContact] = useState(null);

  /**
   * After a mapping write: the row's badge, the org-wide counts, and — under an
   * active mapping filter — whether the contact still belongs in the list. All
   * three come from the server; nothing is guessed locally.
   */
  const handleMappingChanged = useCallback(() => {
    setMappingContact(null);
    reload();
    loadCounts(debouncedQ);
  }, [reload, loadCounts, debouncedQ]);

  // Memoised because the two useMemos below depend on it: `items || []` is a new
  // array identity on every render, which would defeat their caching.
  const loadedRows = useMemo(() => items || [], [items]);

  // The server applied the mapping filter, so what came back IS the filtered set.
  const rows = loadedRows;

  const isFiltered = Boolean(filters.q || filters.mapping);
  // What the current rows were actually fetched/filtered with.
  const queryIsFiltered = Boolean(debouncedQ || filters.mapping);
  const sel = selected ? statuses[selected.bexio_contact_id] : null;

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-foreground">Bexio-Kontakte</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Synchronisierte Bexio-Kontakte und ihre Vantio-Zuordnungen.
        </p>
      </header>

      <section
        aria-label="Kennzahlen"
        className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
      >
        <BexioKpiFilterCard
          icon={Users}
          tone="primary"
          label="Gesamt Kontakte"
          value={allTotal ?? "—"}
          hint="In Bexio gespiegelt"
          active={!filters.mapping}
          onSelect={() => applyFilters({ ...filters, mapping: "" })}
          selectHint="Zuordnungsfilter zurücksetzen"
        />
        <BexioKpiFilterCard
          icon={CheckCircle2}
          tone="good"
          label="Zugeordnet"
          value={mappedTotal ?? "—"}
          hint="Mit gespeicherter Zuordnung"
          active={filters.mapping === "mapped"}
          onSelect={() => applyFilters({ ...filters, mapping: "mapped" })}
        />
        {/* Broader than the service's `unmapped` status on purpose — see the
            options above. The card says "Ohne Zuordnung", which is what it counts. */}
        <BexioKpiFilterCard
          icon={MinusCircle}
          tone={unmappedTotal ? "warn" : "neutral"}
          label="Ohne Zuordnung"
          value={unmappedTotal ?? "—"}
          hint="Noch keine Zuordnung gespeichert"
          active={filters.mapping === "unmapped"}
          onSelect={() => applyFilters({ ...filters, mapping: "unmapped" })}
        />
      </section>

      <div className="mb-4">
        <BexioFilterBar
          searchValue={filters.q}
          onSearchChange={(q) => applyFilters({ ...filters, q })}
          searchPlaceholder="Name, E-Mail oder Kontakt-Nr. suchen …"
          searchLabel="Kontakte suchen"
          searchHint="Suche und Zuordnungsfilter wirken serverseitig über alle Seiten."
          onReset={() => applyFilters(EMPTY_FILTERS)}
          canReset={isFiltered}
          resultLabel={
            typeof total === "number"
              ? `${total} ${total === 1 ? "Kontakt" : "Kontakte"}`
              : undefined
          }
        >
          <BexioFilterSelect
            label="Zuordnungsstatus"
            value={filters.mapping}
            onChange={(mapping) => applyFilters({ ...filters, mapping })}
            options={MAPPING_FILTER_OPTIONS}
          />
        </BexioFilterBar>
      </div>

      <div className="mb-3">
        <BexioActiveFilters
          chips={[
            filters.mapping && {
              key: "mapping",
              label:
                filters.mapping === "mapped" ? "Zugeordnet" : "Ohne Zuordnung",
              onRemove: () => applyFilters({ ...filters, mapping: "" }),
            },
            filters.q && {
              key: "q",
              label: `Suche „${filters.q}“`,
              onRemove: () => applyFilters({ ...filters, q: "" }),
            },
          ]}
          onClearAll={() => applyFilters(EMPTY_FILTERS)}
        />
      </div>

      <section
        aria-label="Kontakte"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        {loading && loadedRows.length === 0 ? (
          <BexioTableSkeleton columns={6} />
        ) : error ? (
          <BexioTableError message={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          /* The pager stays mounted on an empty page. Since UI-4b `total` is
             counted WITH the filter, so a filtered set of 0 says 0 — but an offset
             past the end of a non-empty set still lands here, and without the
             pager the only way back would be discarding every filter. */
          <>
            <BexioTableEmpty
              filtered={queryIsFiltered}
              noun="Kontakte"
              onReset={() => applyFilters(EMPTY_FILTERS)}
            />
            <BexioTablePagination
              total={total}
              offset={offset}
              limit={limit}
              onOffsetChange={setOffset}
              onLimitChange={(next) => {
                setLimit(next);
                setOffset(0);
              }}
              noun="Kontakte"
              busy={loading}
            />
          </>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    {/* See the invoice table: explicit widths, or the name column
                        takes the slack and leaves a gap beside the number. */}
                    <th scope="col" className="w-[9%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Kontakt-Nr.
                    </th>
                    <th scope="col" className="w-[26%] px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Name
                    </th>
                    <th scope="col" className="w-[24%] px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      E-Mail
                    </th>
                    <th scope="col" className="w-[7%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Typ
                    </th>
                    <th scope="col" className="w-[17%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Aktualisiert
                    </th>
                    <th scope="col" className="w-[17%] whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                      Zuordnung
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.id}
                      className={`border-b border-border last:border-0 ${
                        selected?.id === c.id ? "bg-muted" : ""
                      }`}
                    >
                      <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted-foreground">
                        {c.nr || `#${c.bexio_contact_id}`}
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          type="button"
                          onClick={() => setSelected(c)}
                          aria-label={`Details zu ${c.display_name || `Kontakt ${c.bexio_contact_id}`} anzeigen`}
                          className="block max-w-full truncate rounded text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {c.display_name || "Ohne Namen"}
                        </button>
                      </td>
                      <td className="truncate px-4 py-2.5 text-muted-foreground">
                        {c.mail || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {/* Raw Bexio contact_type. Vantio stores no label table for
                            it, so the mirror value is shown as-is rather than
                            translated into a guess. */}
                        {c.contact_type || "—"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                        {formatDateTime(c.bexio_updated_at || c.synced_at)}
                      </td>
                      <td className="px-4 py-2.5">
                        {/* The action lives next to the status, not buried in a
                            drawer: assigning a contact is the main job here. */}
                        <div className="flex items-center gap-2">
                          <MappingPill status={statuses[c.bexio_contact_id]?.mapping_status} />
                          <button
                            type="button"
                            onClick={() => setMappingContact(c)}
                            aria-label={`${contactMappingActionLabel(
                              statuses[c.bexio_contact_id]?.mapping_status
                            )}: ${c.display_name || `Kontakt ${c.bexio_contact_id}`}`}
                            className="shrink-0 whitespace-nowrap rounded-lg border border-border px-2 py-0.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {contactMappingActionLabel(
                              statuses[c.bexio_contact_id]?.mapping_status
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            <BexioTablePagination
              total={total}
              offset={offset}
              limit={limit}
              onOffsetChange={setOffset}
              onLimitChange={(next) => {
                setLimit(next);
                setOffset(0);
              }}
              noun="Kontakte"
              busy={loading}
            />
          </>
        )}
      </section>

      {/* The contact mapping modal moved out of the legacy page — one
          implementation, so the suggestion fetch, the confirm/change/remove calls
          and the no-preselection rule are the ones already in production. */}
      {mappingContact ? (
        <BexioContactMappingModal
          contact={mappingContact}
          currentStatus={statuses[mappingContact.bexio_contact_id]}
          onClose={() => setMappingContact(null)}
          onChanged={handleMappingChanged}
        />
      ) : null}

      <BexioDetailDrawer
        open={selected != null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
        title={selected?.display_name || (selected ? `Kontakt ${selected.bexio_contact_id}` : "")}
        subtitle={selected ? `Kontakt-Nr. ${selected.nr || selected.bexio_contact_id}` : undefined}
        badge={selected ? <MappingPill status={sel?.mapping_status} /> : null}
      >
        {selected ? (
          <>
            <DrawerSection title="Stammdaten">
              <DrawerField label="Name" value={selected.display_name} wide />
              <DrawerField label="E-Mail" value={selected.mail} wide />
              <DrawerField label="Kontakt-Nr." value={selected.nr} />
              <DrawerField label="Bexio-ID" value={String(selected.bexio_contact_id)} mono />
              <DrawerField label="Typ" value={selected.contact_type} />
              <DrawerField
                label="Aktualisiert"
                value={formatDateTime(selected.bexio_updated_at || selected.synced_at)}
                wide
              />
            </DrawerSection>

            <DrawerSection title="Zuordnung">
              <DrawerField
                label="Status"
                value={
                  sel?.mapping_status
                    ? mapLabel(MAPPING_STATUS_LABEL, sel.mapping_status)
                    : null
                }
                wide
              />
              {/* Populated by the mapping response only for a confirmed, resolvable
                  mapping. Absent means unmapped or unresolvable — not unfetched. */}
              <DrawerField label="Mietverhältnis" value={sel?.mapped_tenancy_reference} wide />
              {/* A list: the mapping carries primary tenant plus participants. */}
              <DrawerField
                label={(sel?.mapped_tenant_names?.length || 0) > 1 ? "Mieter:innen" : "Mieter"}
                value={
                  sel?.mapped_tenant_names?.length
                    ? sel.mapped_tenant_names.join(", ")
                    : null
                }
                wide
              />
              <DrawerField label="Einheit" value={sel?.mapped_unit_label} />
              <DrawerField label="Zimmer" value={sel?.mapped_room_name} />
              <DrawerField label="Quelle" value={sel?.match_source} />
              <DrawerField
                label="Bestätigt"
                value={sel?.confirmed_at ? formatDateTime(sel.confirmed_at) : null}
              />
              {/* The same action as in the row, reachable from here too.
                  Two reasons, both found in the visual pass: the mapping column is
                  the LAST one, so at 390px it sits outside the table's horizontal
                  scroll until the reader drags to the far edge — the slice's main new
                  affordance, effectively hidden on a phone. And reading the mapping
                  here only to have to close the drawer to act on it is a dead end.
                  The drawer closes as this opens, so two dialogs are never stacked. */}
              <div className="col-span-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    const c = selected;
                    setSelected(null);
                    setMappingContact(c);
                  }}
                  aria-label={`${contactMappingActionLabel(sel?.mapping_status)}: ${
                    selected.display_name || `Kontakt ${selected.bexio_contact_id}`
                  }`}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {contactMappingActionLabel(sel?.mapping_status)}
                </button>
              </div>
            </DrawerSection>

            {sel?.warnings?.length ? (
              <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide">
                  Hinweise
                </h3>
                <ul className="list-inside list-disc space-y-1">
                  {sel.warnings.map((w) => (
                    <li key={w} className="break-words">
                      {w}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <p className="text-xs text-muted-foreground">
              Ein Bexio-Kontakt kann mehrere Mietverhältnisse bedienen; ein
              Mietverhältnis hat höchstens einen Bexio-Rechnungskontakt. Zuordnungen
              werden im bestehenden Zuordnungs-Dialog gepflegt.
            </p>
          </>
        ) : null}
      </BexioDetailDrawer>
    </div>
  );
}
